import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';

interface Role {
  id: string;
  name: string;
  code: string;
  description: string;
  level: number;
  isSystem: boolean;
}

export default function RoleManagement() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const { hasPermission } = usePermission();
  const canManageRoles = hasPermission('permissions.update');
  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({ name: '', code: '', description: '', level: 0 });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const data = await api.get<{ items: Role[] }>('/api/roles');
        if (cancelled) return;
        setRoles(data.items);
      } catch {
        if (cancelled) return;
        setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const data = await api.get<{ items: Role[] }>('/api/roles');
      setRoles(data.items);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role: Role) => {
    if (role.isSystem) return;
    setEditRole(role);
    setFormData({
      name: role.name,
      code: role.code,
      description: role.description || '',
      level: role.level,
    });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditRole(null);
    setFormData({ name: '', code: '', description: '', level: 0 });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editRole) {
        await api.put(`/api/roles/${editRole.id}`, formData);
      } else {
        await api.post('/api/roles', formData);
      }
      setShowModal(false);
      toast.success(editRole ? 'ロールを更新しました' : 'ロールを追加しました');
      await fetchRoles();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'ロールの保存に失敗しました';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    if (loading) return;
    setDeleteTarget(id);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/roles/${deleteTarget}`);
      toast.success('ロールを削除しました');
      await fetchRoles();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'ロールの削除に失敗しました';
      toast.error(msg);
    } finally {
      setDeleteTarget(null);
    }
  };

  const columns = [
    { key: 'name', header: 'ロール名', minWidth: '150px', render: (role: Role) => (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium">{role.name}</span>
        {role.isSystem && <Badge variant="info">システム</Badge>}
      </div>
    )},
    { key: 'code', header: 'コード', minWidth: '100px' },
    { key: 'description', header: '説明', minWidth: '200px', render: (role: Role) => (
      <span className="text-gray-500 line-clamp-1">{role.description || '-'}</span>
    )},
    { key: 'level', header: 'レベル', minWidth: '80px' },
    { key: 'actions', header: '', minWidth: '120px', render: (role: Role) => (
      role.isSystem ? (
        <span className="text-gray-400 text-sm">編集不可</span>
      ) : (
        <div className="flex gap-1">
          {canManageRoles && <Button variant="ghost" size="sm" onClick={() => handleEdit(role)}>編集</Button>}
          {canManageRoles && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(role.id)}>
              <span className="text-red-500">削除</span>
            </Button>
          )}
        </div>
      )
    )},
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">ロール管理</h1>
          <p className="text-gray-500 mt-1 text-sm">ユーザーの権限グループを管理します</p>
        </div>
        {canManageRoles && (
          <Button onClick={handleCreate} className="w-full sm:w-auto">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            ロールを追加
          </Button>
        )}
      </div>

      <Card>
        {fetchError ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <p className="mb-4">ロールの取得に失敗しました</p>
            <Button variant="secondary" onClick={fetchRoles}>再試行</Button>
          </div>
        ) : (
          <Table columns={columns} data={roles} loading={loading} />
        )}
      </Card>

      <Modal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={editRole ? 'ロールを編集' : 'ロールを追加'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="ロール名"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="例: マネージャー"
            maxLength={100}
            required
          />
          <div>
            <Input
              label="コード"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="例: manager"
              pattern="[a-z0-9_]+"
              title="半角英小文字・数字・アンダースコアのみ使用できます"
              maxLength={50}
              disabled={!!editRole}
              required
            />
            {!!editRole && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">コードは作成後に変更できません</p>
            )}
          </div>
          <Input
            label="説明"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="このロールの説明"
          />
          <Input
            label="レベル（数値が大きいほど上位、1〜100）"
            type="number"
            min={1}
            max={100}
            value={formData.level}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 0;
              setFormData({ ...formData, level: Math.min(100, Math.max(0, val)) });
            }}
          />
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="submit" loading={saving} className="w-full sm:w-auto">
              {editRole ? '更新' : '追加'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="確認" size="sm">
        <p className="text-gray-600 dark:text-gray-300 mb-6">本当に削除しますか？</p>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} className="w-full sm:w-auto">
            キャンセル
          </Button>
          <Button variant="danger" onClick={executeDelete} className="w-full sm:w-auto">
            削除
          </Button>
        </div>
      </Modal>
    </div>
  );
}
